<template>
  <div>
    <page-title>
      <button-group v-if="buttons.head" :buttons="buttons.head.right"
                    :class="getFlexboxClass('right')" class="float-right"
      />

      {{ pageTitle }}

      <b-badge tag="sup" class="badge-pill">
        <vuetable-pagination-info ref="paginationInfoTotal" class="v-align" info-template="{total}" no-data-template="0" />
      </b-badge>
    </page-title>

    <b-card class="card-tab">
      <div slot="header">Filtres</div>
      <vuetable-filter-bar @filter="load" @filter-date="load" class="col-12 d-flex flex-row-reverse" :help-dates="displayHelpFilterDates" :filter-by-date="dateIntervalFilter" />

      <vuetable ref="vuetable"
                :api-url="getApiUrl"
                :fields="fields"
                pagination-path=""
                data-path="data"
                :http-options="apiObject.getAxiosConfig()"
                @vuetable:pagination-data="onPaginationData"
                @vuetable:load-error="onLoadError"
                :css="css.table"
                class="col-12 mt-3"
                :query-params="{'sort': '__sort', 'page': '__page', 'perPage': '__per_page'}"
                :sort-order="sortOrder"
                :no-data-template="$t('_no_data')"
      >
        <!-- Slots for custom fields -->
        <div slot="list-actions" slot-scope="props">
          <button-group v-if="buttonGroup"
                        :buttons="getListActions(props.rowData)"
                        :class="getFlexboxClass('middle')"
                        @needReload="$refs.vuetable.reload()"></button-group>
          <div v-if="buttonGroup === false"  class="float-right">
                  <base-button v-for="button in getListActions(props.rowData)"
                   :key="button.id"
                   :route="button.route"
                   :text="button.text"
                   :title="button.title"
                   :fa-class="button.faClass"
                   :fa-position="button.faPosition"
                   :bootstrap-class="button.bootstrapClass"
                   :deletion-data="button.deletionData"
              />
          </div>
        </div>
        />
        <!-- END Slots for custom fields -->
      </vuetable>

      <div slot="footer" class="list-infos row">
        <vuetable-pagination-info ref="paginationInfo"
                                  info-template="Affichage de l'élément {from} à {to} sur {total} éléments"
                                  no-data-template="Aucun élément"
                                  class="v-align dt-col-foot col-xs-12 col-md-6"
        />
        <vuetable-pagination ref="pagination" :css="css.pagination"
                             @vuetable-pagination:change-page="onPageChange"
                            class="col-xs-12 col-md-6"
        />
      </div>
    </b-card>

    <div v-if="!isNested && buttons.bottom" class="foot row">
      <button-group v-for="(button, position) in buttons.bottom"
                    :key="button.id"
                    v-if="button.length"
                    :buttons="buttons.bottom[position]"
                    :positioning-clues="{
                      overall: buttons.bottom,
                      position: position
                    }"
      />
    </div>
  </div>
</template>

<script>
import Vuetable from 'vuetable-2'
import VuetablePagination from './vuetable/VuetablePaginationBootstrap'
import VuetablePaginationInfo from 'vuetable-2/src/components/VuetablePaginationInfo'
import VuetableFilterBar from './vuetable/VuetableFilterBarBootstrap'
import formatter from '@/components/global/mixins/formatterMethods'
import { flexbox } from '@/utils/style'
import { getListActions, getListButtons, createVueTableFields, getCssConfig } from '@/utils/list'
import AbstractApi from '@/api/abstractApi'
import pageTitle from '@/components/backoffice/pageTitle'
import buttonGroup from '@/components/backoffice/button/group'
import baseButton from '@/components/backoffice/button/base'
import bBadge from 'bootstrap-vue/es/components/badge/badge'
import backOfficeAccessMixin from '../../conf/backOfficeAccessMixin';

export default {
  components: {
    Vuetable,
    VuetablePagination, // nav/button based pagination
    VuetablePaginationInfo,
    VuetableFilterBar, // Search
    pageTitle,
    buttonGroup,
    baseButton,
    bBadge
  },

  mixins: [formatter, backOfficeAccessMixin],

  props: {
    entityName: {
      type: String,
      required: true
    },
    actionCol: {
      type: Boolean,
      default: true
    },
    isNested: {
      type: Boolean,
      default: false
    },
    buttonGroup: {
      type: Boolean,
      default: true
    },
    title: {
      type: String,
      default: ''
    },
    parentApiDetails: Object,
    overrides: {
      type: Object
    }
  },

  data () {
    return {
      fields: [], // This is populated in created event
      css: getCssConfig(),
      pageTitle: '',
      apiObject: Object,
      translationPrefix: '',
      buttons: Object,
      filter: '',
      dateIntervalFilter: {
        startDate: null,
        endDate: null
      }
    }
  },

  created () {
    this.setApiObject()
    this.setTranslationPrefix()
    this.setButtons()
    this.setFields()
    this.setPageTitle()
    this.setDefaultDateIntervalFilter()
  },

  mounted () {
    if (this.isNested) {
      // set parent id for create requests
      for (let i in this.buttons) {
        if (this.buttons.hasOwnProperty(i)) {
          let vPosition = this.buttons[i]
          for (let j in vPosition) {
            if (vPosition.hasOwnProperty(j)) {
              let hPosition = vPosition[j]
              for (let button of hPosition) {
                if (button.name === 'create') {
                  button.route.params = {
                    parentEntity: this.parentApiDetails.entityName,
                    id: this.parentApiDetails.id
                  }
                }
              }
            }
          }
        }
      }
    }
  },

  methods: {
    load (search) {
      this.filter = search.filterText
      this.dateIntervalFilter = {
        startDate: search.filterStartDate,
        endDate: search.filterEndDate
      }
      this.$refs.vuetable.reload()
    },

    onPaginationData (paginationData) {
      this.$refs.pagination.setPaginationData(paginationData)
      this.$refs.paginationInfo.setPaginationData(paginationData)
      this.$refs.paginationInfoTotal.setPaginationData(paginationData)
    },

    onPageChange (page) {
      this.$refs.vuetable.changePage(page)
    },

    onLoadError (response) {
      // unauthorized, probably timed out
      if (response.response.status === 403) {
        this.$router.push({ path: '/login' })
      }
    },

    setApiObject () {
      this.apiObject = new AbstractApi(this.entityName, undefined, this.overrides)
    },

    setButtons () {
      this.buttons = getListButtons(
        this.apiObject.getEntityName(),
        this.displayOnlyShowButton,
        this.apiObject.getConf('list', 'buttons'),
        this.parentApiDetails && this.parentApiDetails.id ? this.parentApiDetails.id : null,
        this.parentApiDetails && this.parentApiDetails.entityName ? this.parentApiDetails.entityName : null,
      )
    },

    setFields () {
      this.fields = createVueTableFields(
        this.translationPrefix,
        this.apiObject.getConf('list', 'fields'),
        this.apiObject.getConf('fields'),
        this.actionCol
      )
    },

    setPageTitle () {
      // if title is defined in props
      if (this.title) {
        this.pageTitle = this.title
        return
      }

      // else get title from translations
      let titleAuto = this.translationPrefix + '.page_title'

      if (this.$i18n.te(titleAuto)) {
        this.pageTitle = this.$i18n.t(titleAuto)
      } else {
        // this.pageTitle = this.$i18n.t(this.apiObject.getEntityName() + '.list.page_title') + ' ' + this.$i18n.tc('_' + this.apiObject.getEntityName(), 2)
        this.pageTitle = this.$i18n.t(this.apiObject.getEntityName() + '.list.page_title')
      }
    },

    setTranslationPrefix () {
      this.translationPrefix = this.apiObject.getListTranslationPrefix()
    },

    setDefaultDateIntervalFilter () {
      if (!this.isDisplayDatefilterActive()) {
        return
      }
      let dateIntervalData = this.apiObject.getConf('list', 'filterInput').dateInterval
      let today = new Date(Date.now())
      let beginningDate = new Date(today.getTime() + (dateIntervalData.start * 24 * 60 * 60 * 1000))
      let endDate = new Date(today.getTime() + (dateIntervalData.end * 24 * 60 * 60 * 1000))

      this.dateIntervalFilter.startDate = this.formatDateInterval(beginningDate)
      this.dateIntervalFilter.endDate = this.formatDateInterval(endDate)
    },

    formatDateInterval (date) {
      let days = date.getDate().toString().padStart(2, '0')
      let months = (date.getMonth() + 1).toString().padStart(2, '0')
      let years = date.getFullYear()
      return years + '-' + months + '-' + days
    },

    getFlexboxClass (position) {
      return flexbox.position.justifyContent(position)
    },

    getListActions (entity) {
      return getListActions(
        this.apiObject.getConf('entityName'),
        this.apiObject.getConf('list', 'actions'),
        this.displayOnlyShowButton,
        entity.id,
        entity,
        this.apiObject.getConf('list', 'allowedActions')
      )
    },

    isDisplayDatefilterActive () {
      return this.apiObject.conf.list && this.apiObject.conf.list.filterInput && this.apiObject.conf.list.filterInput.dateInterval
    }
  },

  computed: {
    getApiUrl () {
      return this.apiObject.getListUrl(
        this.isNested ? this.parentApiDetails.entityName : null,
        this.isNested ? this.parentApiDetails.id : null,
        this.filter !== '' ? this.filter : null,
        this.isDisplayDatefilterActive() ? {
          start: this.dateIntervalFilter.startDate,
          end: this.dateIntervalFilter.endDate,
          field: this.apiObject.getConf('list', 'filterInput').dateInterval.fieldAs
        } : null
      )
    },

    sortOrder () {
      return this.apiObject.conf.list.sortOrder || []
    },

    displayHelpFilterDates () {
      let fieldNames = this.fields.map(f => f.name)

      return this.apiObject.conf.fields.some(f =>
        f.type === 'dateTime' && fieldNames.includes(f.name)
      )
    },

    displayDateIntervalFilter () {
      return this.isDisplayDatefilterActive()
    }

  }
}
</script>
